import { Button, Icon, Switch } from '@platform/ui-library';
import {
  ChangeEvent,
  FormEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import { ErrorMessage, PinInput } from '@app/form_components';
import { useServices } from '@app/hooks';
import {
  LogindWithMfaState,
  LoginEnableMfaState,
  LoginState
} from './login-state';
import styles from './login-code-form.module.scss';

export type LoginCodeFormProps = {
  onComplete: () => void;
  state: LoginEnableMfaState | LogindWithMfaState;
  setState: (values: LoginState) => void;
};

export function LoginCodeForm({
  onComplete,
  state,
  setState
}: LoginCodeFormProps) {
  const { userService } = useServices();
  const contentRef = useRef<HTMLFormElement>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => {
      contentRef.current?.querySelector('input')?.focus();
    });
  }, []);

  const {
    code,
    enableRememberMe,
    isMfaEnabled
  } = state;
  const qrCode = (isMfaEnabled) ? null : `data:image/png;base64, ${state.qrCode}`;

  const message = (isMfaEnabled) ? (
    'Please enter the six-digit code generated by your authenticator app.'
  ) : [
    'Enable two-factor authentication for enhance account security.',
    'Scan QR code from an authenticator app and enter six-digit code before logging in.'
  ].join('\n');

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!code) {
      setErrorMessage('Enter code before proceeding.');
    }

    setIsLoading(true);
    try {
      await userService.login(state);
      onComplete();
      setIsLoading(false);
    } catch (error) {
      setErrorMessage((error instanceof Error) ? (
        error.message
      ) : 'An unknown error occurred');
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} ref={contentRef}>
      <div className="box header">
        <a onClick={() => setState({ ...state, validUsernameAndPassword: false })}>
          <Icon icon='arrowLeft' />
        </a>
        <h2>
          {(!isMfaEnabled) ? 'Enable ' : ''}
          Multi-Factor Authentication
        </h2>
      </div>
      <div className="box content">
        <p className="space-pre-wrap">{message}</p>
        {(!qrCode) ? null : (
          <div className={styles.qr}>
            <img alt='QR Code' src={qrCode} />
          </div>
        )}
        <label>Enter Verification Code</label>
        <PinInput
          onComplete={(newCode) => setState({ ...state, code: newCode })}
          defaultValue={code}
        />
        <ErrorMessage message={errorMessage} />
      </div>
      <div className="box footer no-border space-between">
        <Switch
          checked={enableRememberMe}
          inputFirst
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setState({ ...state, enableRememberMe: e.target.checked });
          }}
          label="Remember me"
        />
        <Button type="submit">
          {(isLoading) ? 'Loading...' : 'Verify & Login'}
        </Button>
      </div>
    </form>
  );
}
